/**
 * Retry utility with exponential backoff.
 *
 * HTTP 429 is retryable when the server returns `retry_after` / `Retry-After`,
 * because TAGP's Redis rate limiter does not count rejected requests into the
 * sliding window (safe to wait and retry).
 */

/** Loose shape for API / HTTP / network errors inspected by retry helpers. */
interface RetryErrorLike {
  data?: {
    retry_after?: number | string
    retryAfter?: number
    error_code?: string
    error?: string
    detail?: string
  }
  headers?: Record<string, unknown>
  response?: {
    status?: number
    headers?: {
      get?: (name: string) => string | null
      [key: string]: unknown
    }
  }
  status?: number
  error_code?: string
  code?: string
  message?: string
}

export interface RetryOptions {
  maxRetries?: number
  initialDelay?: number
  maxDelay?: number
  backoffMultiplier?: number
  retryable?: (error: unknown) => boolean
  /** Optional override for delay between attempts (ms). */
  delayMs?: (error: unknown, attemptIndex: number, currentDelay: number) => number
}

const DEFAULT_OPTIONS: Required<Omit<RetryOptions, 'delayMs'>> & {
  delayMs: RetryOptions['delayMs']
} = {
  maxRetries: 3,
  initialDelay: 1000,
  maxDelay: 60000,
  backoffMultiplier: 2,
  retryable: () => true,
  delayMs: undefined,
}

/**
 * Sleep for a given number of milliseconds.
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Extract Retry-After / retry_after from an ApiError-like object (seconds → ms).
 * Returns null when the server did not advertise a wait time.
 */
export function getRateLimitRetryDelayMs(error: unknown): number | null {
  const err = error as RetryErrorLike
  const data = err?.data
  const fromBody =
    (typeof data?.retry_after === 'number' && data.retry_after) ||
    (typeof data?.retryAfter === 'number' && data.retryAfter) ||
    (typeof data?.retry_after === 'string' && Number(data.retry_after)) ||
    null

  if (typeof fromBody === 'number' && Number.isFinite(fromBody) && fromBody > 0) {
    return Math.min(Math.ceil(fromBody * 1000), 120_000)
  }

  const headerVal =
    err?.headers?.['retry-after'] ||
    err?.headers?.['Retry-After'] ||
    err?.response?.headers?.get?.('Retry-After') ||
    err?.response?.headers?.['retry-after']

  if (headerVal != null) {
    const sec = typeof headerVal === 'number' ? headerVal : Number(String(headerVal).trim())
    if (Number.isFinite(sec) && sec > 0) {
      return Math.min(Math.ceil(sec * 1000), 120_000)
    }
  }

  return null
}

/**
 * Retry a function with exponential backoff.
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const opts = { ...DEFAULT_OPTIONS, ...options }
  let lastError: unknown
  let delay = opts.initialDelay

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error

      // Check if error is retryable
      if (!opts.retryable(error)) {
        throw error
      }

      // Don't wait after the last attempt
      if (attempt < opts.maxRetries) {
        const fromServer = getRateLimitRetryDelayMs(error)
        const waitMs = opts.delayMs
          ? opts.delayMs(error, attempt, delay)
          : fromServer ?? delay
        await sleep(Math.min(waitMs, opts.maxDelay))
        delay = Math.min(delay * opts.backoffMultiplier, opts.maxDelay)
      }
    }
  }

  throw lastError
}

/**
 * Non-retryable error codes that indicate configuration/permanent issues.
 * These errors won't be fixed by retrying.
 */
const NON_RETRYABLE_ERROR_CODES = new Set([
  'CONNECTOR_ERROR', // Database/connector configuration issues
  'VALIDATION_ERROR', // Invalid request data
  'NOT_FOUND', // Resource doesn't exist
  'FORBIDDEN', // Permission denied
  'UNAUTHORIZED', // Authentication required
  'CONFLICT', // Duplicate/conflict
  'INTERNAL_SERVER_ERROR', // Internal server errors (including database not found)
])

/**
 * Check if an error is retryable (network errors, transient 5xx, rate limits).
 *
 * Returns false for:
 * - Configuration errors (CONNECTOR_ERROR)
 * - Validation errors
 * - Permission/auth errors
 * - Resource not found
 * - Other 4xx client errors (except 429)
 *
 * Returns true for:
 * - Network/connection errors
 * - Transient 5xx errors (except CONNECTOR_ERROR)
 * - HTTP 429 / RATE_LIMIT_EXCEEDED (honor Retry-After via getRateLimitRetryDelayMs)
 */
export function isRetryableError(error: unknown): boolean {
  const err = error as RetryErrorLike

  // First check for specific non-retryable error codes from our API
  // Check error.data.error_code and error.error_code (in case error_code is at top level)
  const errorCode = err?.data?.error_code || err?.error_code || err?.code
  if (errorCode === 'RATE_LIMIT_EXCEEDED') {
    return true
  }
  if (errorCode && NON_RETRYABLE_ERROR_CODES.has(errorCode)) {
    return false
  }

  // Check error message for database/connector errors that shouldn't be retried
  const errorMessage = (
    err?.message ||
    err?.data?.error ||
    err?.data?.detail ||
    ''
  ).toLowerCase()
  if (
    errorMessage.includes('database') &&
    (errorMessage.includes('does not exist') ||
      errorMessage.includes('not found') ||
      errorMessage.includes('not accessible'))
  ) {
    return false
  }

  // For errors with status property (ApiError)
  if (err?.status !== undefined) {
    const status = err.status

    // Rate limit — safe to retry after Retry-After (rejected requests are not counted)
    if (status === 429) {
      return true
    }

    // Never retry other 4xx errors (client errors)
    if (status >= 400 && status < 500) {
      return false
    }

    // For 5xx errors, check if it's a non-retryable type
    if (status >= 500) {
      // Check error code if available (try multiple paths)
      const code = err?.data?.error_code || err?.error_code || err?.code
      if (code && NON_RETRYABLE_ERROR_CODES.has(code)) {
        return false
      }
      // Retry other 5xx errors (transient server issues)
      return true
    }
  }

  // Network errors (fetch failures)
  if (error instanceof TypeError && error.message.includes('fetch')) {
    return true
  }

  // HTTP errors with response object
  if (err?.response) {
    const status = err.response.status as number

    if (status === 429) {
      return true
    }

    // Never retry other 4xx errors
    if (status >= 400 && status < 500) {
      return false
    }

    // Only retry 5xx errors (transient server issues)
    return status >= 500
  }

  // Connection/network errors
  if (
    err?.message &&
    (err.message.includes('network') ||
      err.message.includes('timeout') ||
      err.message.includes('ECONNREFUSED') ||
      err.message.includes('ENOTFOUND') ||
      err.message.includes('Failed to fetch'))
  ) {
    return true
  }

  // Default: don't retry unknown errors
  return false
}
